/**
 * Attachment Cache Service
 * Handles caching attachments in IndexedDB for offline viewing
 * 
 * Strategy:
 * - Cache attachments for events in the next 7 days
 * - 100MB total cache limit with LRU eviction
 * - Store file blobs with metadata
 * - Support pending attachments for new events (before save)
 */

const DB_NAME = 'travel-itinerary-attachments';
const DB_VERSION = 2; // Bumped for pending store
const STORE_NAME = 'attachments';
const META_STORE = 'meta';
const PENDING_STORE = 'pending'; // Store for attachments awaiting upload

const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB
const CACHE_DAYS_AHEAD = 7;

let db = null;
let cachedEventsData = []; // Store events data for eviction protection

/**
 * Initialize the attachment cache database
 */
export async function initAttachmentCache() {
  if (db) return db;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Store for attachment blobs (cached from server)
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('eventId', 'eventId', { unique: false });
        store.createIndex('expenseId', 'expenseId', { unique: false });
        store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
      }
      
      // Store for cache metadata
      if (!database.objectStoreNames.contains(META_STORE)) {
        database.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      
      // Store for pending attachments (awaiting upload after event creation)
      if (!database.objectStoreNames.contains(PENDING_STORE)) {
        const pendingStore = database.createObjectStore(PENDING_STORE, { keyPath: 'tempId' });
        pendingStore.createIndex('targetTempId', 'targetTempId', { unique: false });
      }
    };
  });
}

/**
 * Get database instance
 */
async function getDB() {
  if (!db) {
    await initAttachmentCache();
  }
  return db;
}

/**
 * Cache an attachment blob
 */
export async function cacheAttachment(id, blob, metadata) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction([STORE_NAME, META_STORE], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const record = {
      id,
      blob,
      eventId: metadata.event_id || null,
      expenseId: metadata.expense_id || null,
      filename: metadata.filename,
      fileType: metadata.file_type,
      fileSize: metadata.file_size,
      cachedAt: Date.now(),
      lastAccessed: Date.now(),
    };
    
    store.put(record);
    
    tx.oncomplete = () => {
      // Update total cache size and evict if needed
      updateCacheSize().then(resolve).catch(resolve);
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get an attachment from cache
 */
export async function getAttachmentFromCache(id) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const request = store.get(id);
    
    request.onsuccess = () => {
      const result = request.result;
      
      if (result) {
        // Update last accessed time
        result.lastAccessed = Date.now();
        store.put(result);
        resolve(result);
      } else {
        resolve(null);
      }
    };
    
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove an attachment from cache
 */
export async function removeFromCache(id) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    store.delete(id);
    
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Clear all cached attachments for a specific event
 * Used for cascade delete when event is deleted
 */
export async function clearCacheByEventId(eventId) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('eventId');
    
    const request = index.openCursor(IDBKeyRange.only(eventId));
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all cached attachment IDs
 */
export async function getCachedAttachmentIds() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const request = store.getAllKeys();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Calculate current cache size
 */
export async function getCacheSize() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    let totalSize = 0;
    const request = store.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        totalSize += cursor.value.fileSize || 0;
        cursor.continue();
      } else {
        resolve(totalSize);
      }
    };
    
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update cache size and evict old items if needed
 * Protected: attachments for events in the next 7 days are NOT evicted
 */
async function updateCacheSize() {
  const currentSize = await getCacheSize();
  
  if (currentSize <= MAX_CACHE_SIZE) {
    return;
  }
  
  console.log(`[AttachmentCache] Cache size ${(currentSize / 1024 / 1024).toFixed(2)}MB exceeds limit, evicting...`);
  
  const database = await getDB();
  
  // Calculate protected event IDs (events in the next 7 days)
  const now = new Date();
  const futureDate = new Date(now.getTime() + (CACHE_DAYS_AHEAD * 24 * 60 * 60 * 1000));
  const protectedEventIds = new Set(
    cachedEventsData
      .filter(event => {
        const eventDate = new Date(event.start_datetime);
        return eventDate >= now && eventDate <= futureDate;
      })
      .map(e => e.id)
  );
  
  // Get all items sorted by lastAccessed (oldest first)
  const items = await new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('lastAccessed');
    
    const request = index.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  
  // Separate into protected and evictable items
  const evictableItems = items.filter(item => !protectedEventIds.has(item.eventId));
  const protectedItems = items.filter(item => protectedEventIds.has(item.eventId));
  
  // Sort evictable by lastAccessed ascending (oldest first)
  evictableItems.sort((a, b) => a.lastAccessed - b.lastAccessed);
  
  // Calculate how much to free
  let sizeToFree = currentSize - MAX_CACHE_SIZE + (10 * 1024 * 1024); // Free extra 10MB buffer
  
  // Only evict from non-protected items
  for (const item of evictableItems) {
    if (sizeToFree <= 0) break;
    
    await removeFromCache(item.id);
    sizeToFree -= item.fileSize || 0;
    console.log(`[AttachmentCache] Evicted ${item.filename}`);
  }
  
  // If still over limit but only protected items remain, log warning
  if (sizeToFree > 0 && evictableItems.length === 0 && protectedItems.length > 0) {
    console.warn(`[AttachmentCache] Cache over limit but only protected items remain (${protectedItems.length} items for upcoming events)`);
  }
}

/**
 * Pre-cache attachments for upcoming events
 * Called during sync
 */
export async function preCacheAttachments(events, attachmentsApi) {
  // Store events data for eviction protection
  cachedEventsData = events || [];
  
  const now = new Date();
  const futureDate = new Date(now.getTime() + (CACHE_DAYS_AHEAD * 24 * 60 * 60 * 1000));
  
  // Filter events in the next 7 days
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.start_datetime);
    return eventDate >= now && eventDate <= futureDate;
  });
  
  if (upcomingEvents.length === 0) {
    console.log('[AttachmentCache] No upcoming events to cache');
    return;
  }
  
  console.log(`[AttachmentCache] Pre-caching attachments for ${upcomingEvents.length} upcoming events`);
  
  // Get already cached IDs
  const cachedIds = new Set(await getCachedAttachmentIds());
  
  // Collect all event IDs
  const eventIds = upcomingEvents.map(e => e.id);
  
  // Fetch attachments for these events
  for (const eventId of eventIds) {
    try {
      const attachments = await attachmentsApi.list({ event_id: eventId });
      
      for (const attachment of attachments) {
        // Skip if already cached
        if (cachedIds.has(attachment.id)) {
          continue;
        }
        
        // Check cache size before downloading
        const currentSize = await getCacheSize();
        if (currentSize + attachment.file_size > MAX_CACHE_SIZE) {
          console.log('[AttachmentCache] Cache full, stopping pre-cache');
          return;
        }
        
        try {
          console.log(`[AttachmentCache] Caching ${attachment.filename}`);
          const { blob } = await attachmentsApi.download(attachment.id);
          await cacheAttachment(attachment.id, blob, attachment);
        } catch (downloadErr) {
          console.warn(`[AttachmentCache] Failed to cache ${attachment.filename}:`, downloadErr);
        }
      }
    } catch (err) {
      console.warn(`[AttachmentCache] Failed to fetch attachments for event ${eventId}:`, err);
    }
  }
  
  console.log('[AttachmentCache] Pre-caching complete');
}

/**
 * Clear entire attachment cache
 */
export async function clearAttachmentCache() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    store.clear();
    
    tx.oncomplete = () => {
      console.log('[AttachmentCache] Cache cleared');
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cache statistics
 */
export async function getCacheStats() {
  const database = await getDB();
  
  const size = await getCacheSize();
  const ids = await getCachedAttachmentIds();
  
  return {
    count: ids.length,
    sizeBytes: size,
    sizeMB: (size / 1024 / 1024).toFixed(2),
    maxSizeMB: (MAX_CACHE_SIZE / 1024 / 1024).toFixed(0),
    percentUsed: ((size / MAX_CACHE_SIZE) * 100).toFixed(1),
  };
}

// ============================================
// PENDING ATTACHMENTS (for new events)
// ============================================

/**
 * Generate a unique temporary ID for pending attachments
 */
export function generatePendingId() {
  return `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add a pending attachment (stored locally until event is saved)
 */
export async function addPendingAttachment(file, targetTempId, targetType = 'event') {
  const database = await getDB();
  
  // Read file as ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();
  
  const record = {
    tempId: generatePendingId(),
    blob: arrayBuffer,
    filename: file.name,
    fileType: file.type,
    fileSize: file.size,
    targetTempId, // The temp ID of the event/expense this belongs to
    targetType,   // 'event' or 'expense'
    createdAt: Date.now(),
  };
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    
    store.put(record);
    
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all pending attachments for a target (event/expense by temp ID)
 */
export async function getPendingAttachments(targetTempId) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(PENDING_STORE, 'readonly');
    const store = tx.objectStore(PENDING_STORE);
    const index = store.index('targetTempId');
    
    const request = index.getAll(targetTempId);
    
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get count of pending attachments for a target
 */
export async function getPendingAttachmentCount(targetTempId) {
  const pending = await getPendingAttachments(targetTempId);
  return pending.length;
}

/**
 * Remove a single pending attachment
 */
export async function removePendingAttachment(tempId) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    
    store.delete(tempId);
    
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Check if a file with the same name already exists in pending attachments
 */
export async function hasPendingDuplicate(targetTempId, filename) {
  const pending = await getPendingAttachments(targetTempId);
  return pending.some(p => p.filename === filename);
}

/**
 * Upload all pending attachments for a target after it's been saved
 * Returns { succeeded: number, failed: number, errors: string[] }
 */
export async function uploadPendingAttachments(targetTempId, realId, targetType, attachmentsApi) {
  const pending = await getPendingAttachments(targetTempId);
  
  if (pending.length === 0) {
    return { succeeded: 0, failed: 0, errors: [] };
  }
  
  console.log(`[AttachmentCache] Uploading ${pending.length} pending attachments for ${targetType} ${realId}`);
  
  const results = { succeeded: 0, failed: 0, errors: [] };
  
  for (const item of pending) {
    try {
      // Create a File object from the stored ArrayBuffer
      const file = new File([item.blob], item.filename, { type: item.fileType });
      
      // Upload to server
      const uploadParams = targetType === 'event' 
        ? { event_id: realId }
        : { expense_id: realId };
      
      await attachmentsApi.upload(file, uploadParams);
      
      // Remove from pending store on success
      await removePendingAttachment(item.tempId);
      results.succeeded++;
      
      console.log(`[AttachmentCache] Uploaded ${item.filename}`);
    } catch (err) {
      console.error(`[AttachmentCache] Failed to upload ${item.filename}:`, err);
      results.failed++;
      results.errors.push(`${item.filename}: ${err.message}`);
    }
  }
  
  return results;
}

/**
 * Clear all pending attachments for a target (e.g., if user cancels)
 */
export async function clearPendingAttachments(targetTempId) {
  const pending = await getPendingAttachments(targetTempId);
  
  for (const item of pending) {
    await removePendingAttachment(item.tempId);
  }
  
  console.log(`[AttachmentCache] Cleared ${pending.length} pending attachments for ${targetTempId}`);
}

/**
 * Get all unique target IDs that have pending attachments
 * Returns array of { targetTempId, targetType } objects
 */
export async function getAllPendingTargetIds() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const tx = database.transaction(PENDING_STORE, 'readonly');
    const store = tx.objectStore(PENDING_STORE);
    
    const request = store.getAll();
    
    request.onsuccess = () => {
      const allPending = request.result || [];
      
      // Group by targetTempId and get unique entries
      const targetMap = new Map();
      for (const item of allPending) {
        if (!targetMap.has(item.targetTempId)) {
          targetMap.set(item.targetTempId, {
            targetTempId: item.targetTempId,
            targetType: item.targetType,
          });
        }
      }
      
      resolve(Array.from(targetMap.values()));
    };
    
    request.onerror = () => reject(request.error);
  });
}
